import React, { useState, useEffect, useRef } from "react";
import { Card } from "antd";
import { pathOr } from "ramda";
import { connect } from "react-redux";
import moment from "moment-timezone";
import { CSVLink } from "react-csv";
import { useHistory } from "react-router-dom";

import CustomButton from "../../components/customButton";
import Layout from "../../components/layout";
import CommonTable from "../../components/commonTable";
import ConfirmPopup from "../../components/confirmPopup";
import AddEditSchedule from "../../layout/broadcast/components/addEditSchedule";
import {
  DATE_TIME_FORMAT,
  MOBILE_WIDTH,
  broadcastReportsColumns,
} from "../../constants/constants";
import CommonService from "../../service/CommonService";
import BroadcastService from "../../service/BroadcastUserService";
import CustomNotification from "../../components/notification";
import { setPopup } from "../../actions/popupAction";
import { downloadPatientFilesSagaAction } from "../../sagas/saga-fileDownload";
import loginService from "../../service/login";
import { MessageLogIcon, selectedFile } from "../../assets/svg";
import useWindowDimensions from "../../components/custom-hooks/useWindowDimensions";
import MobileHeader from "../../components/header/mobileHeader";
import "./styles.scss";

const { MM_DD_YYYY_HH_mm } = DATE_TIME_FORMAT;

const BroadcastReport = (props) => {
  const commonService = new CommonService();
  const broadcast = new BroadcastService();

  const history = useHistory();

  const csvLink = useRef();
  const downloadRef = useRef([]);
  const { width } = useWindowDimensions();

  const [state, setState] = useState({
    csvData: "",
    filename: "",
    downloading: {},
    confirmPopup: false,
    editBroadcast: false,
    killPopup: false,
    sendPopup: false,
    record: {},
    onMountFilters: {},
    tableData: undefined,
    updateTable: false,
    broadcastAccess: false,
  });

  const fetchReports = async (filters) => {
    setState((st) => ({
      ...st,
      onMountFilters: filters,
    }));

    const data = await commonService.getBroadcastList(
      filters.page,
      15,
      filters.order,
      filters.search ? encodeURIComponent(JSON.stringify(filters.search)) : ""
    );

    const zone = moment.tz.guess(true);
    data.data.forEach((item) => {
      const valUtc = item.createdAt;
      const valZone = moment.tz(valUtc, zone);
      const valZoneFormatted = valZone.format(MM_DD_YYYY_HH_mm);
      item.createdAt = valZoneFormatted;
      if (item.scheduledDateTime) {
        const scheduleDateTimeValZone = moment.tz(item.scheduledDateTime, zone);
        item.scheduledDateTime = scheduleDateTimeValZone.format(
          MM_DD_YYYY_HH_mm
        );
      } else {
        item.scheduledDateTime = valZoneFormatted;
      }
    });

    return { rows: data.data, count: data.count };
  };

  const getSuggestions = async (column, value) => {
    const data = await commonService.getBroadcastListSuggestions(column, value);
    return data;
  };

  const downloadFiles = async (broadcastTransactionId, filename, modality) => {
    try {
      if (((downloadRef && downloadRef.current) || []).includes(filename))
        return;
      if (downloadRef && downloadRef.current) {
        downloadRef.current.push(filename);
      } else {
        downloadRef.current = [filename];
      }

      setState((st) => ({
        ...st,
        downloading: {
          ...st,
          downloading: { ...st.downloading, [filename]: true },
        },
      }));

      const data = await broadcast.downloadBroadcastFile(
        broadcastTransactionId,
        modality
      );

      setState((st) => ({
        ...st,
        downloading: { ...st.downloading, [filename]: false },
        filename,
        csvData: data,
      }));

      setImmediate(
        () =>
          csvLink &&
          csvLink.current &&
          csvLink.current.link &&
          csvLink.current.link.click()
      );
    } catch (err) {
      props.setPopup({
        customPopupMessage: true,
        message: pathOr(
          "Something went wrong",
          ["response", "data", "message"],
          err
        ),
        messageType: "error",
      });

      setState((st) => ({
        ...st,
        downloading: { ...st.downloading, [filename]: false },
      }));
    }

    downloadRef.current =
      downloadRef &&
      downloadRef.current &&
      downloadRef.current.filter((i) => i !== filename);
  };

  const hideReport = () => {
    if (onlyReportAccess() && width < MOBILE_WIDTH) {
      history.push("/chat/report");
    } else {
      history.push("/broadcast");
    }
  };

  const onlyReportAccess = () => {
    if (
      loggedinUser?.reportOnlyAccess &&
      loggedinUser?.liveChatAccess &&
      loggedinUser?.broadcastAccess
    ) {
      return false;
    } else if (
      loggedinUser?.reportOnlyAccess &&
      loggedinUser?.liveChatAccess &&
      !loggedinUser?.broadcastAccess
    ) {
      return true;
    } else if (
      loggedinUser?.reportOnlyAccess &&
      !loggedinUser?.liveChatAccess &&
      loggedinUser?.broadcastAccess
    ) {
      return false;
    } else if (
      loggedinUser?.reportOnlyAccess &&
      !loggedinUser?.liveChatAccess &&
      !loggedinUser?.broadcastAccess
    ) {
      return true;
    }
    return false;
  };

  const handlePopUp = (type, Data) => {
    if (type === "delete") {
      setState((st) => ({ ...st, confirmPopup: true }));
    }

    if (type === "edit") {
      setState((st) => ({ ...st, editBroadcast: true }));
    }

    if (type === "send") {
      setState((st) => ({ ...st, sendPopup: true }));
    }
    if (type === "killed") {
      setState((st) => ({ ...st, killPopup: true }));
    }
    setState((st) => ({ ...st, record: Data }));
  };

  const handleDeleteBroadcast = async () => {
    const { record } = state;

    try {
      const response = await commonService.deleteBroadcast(
        record.broadcastTransactionId
      );

      props.setPopup({
        customPopupMessage: true,
        messageType: "success",
        message: pathOr("Something went wrong", ["data", "message"], response),
      });

      setState((st) => ({ ...st, confirmPopup: false }));

      const data = await fetchReports(state.onMountFilters);

      setState((st) => ({ ...st, tableData: data }));
    } catch (err) {
      props.setPopup({
        customPopupMessage: true,
        messageType: "error",
        message: pathOr(
          "Something went wrong",
          ["response", "data", "message"],
          err
        ),
      });

      setState((st) => ({ ...st, confirmPopup: false }));
    }
  };

  const killBroadcast = async () => {
    const { record } = state;

    try {
      const response = await commonService.killBroadcast(
        record.broadcastTransactionId
      );

      props.setPopup({
        customPopupMessage: true,
        messageType: "success",
        message: pathOr("Something went wrong", ["data", "message"], response),
      });

      setState((st) => ({ ...st, killPopup: false }));

      const data = await fetchReports(state.onMountFilters);

      setState((st) => ({ ...st, tableData: data }));
    } catch (err) {
      props.setPopup({
        customPopupMessage: true,
        messageType: "error",
        message: pathOr(
          "Something went wrong ",
          ["response", "data", "message"],
          err
        ),
      });

      setState((st) => ({ ...st, killPopup: false }));
    }
  };

  const sendNow = async () => {
    const { record } = state;

    try {
      const response = await commonService.sendNow(
        record.broadcastTransactionId,
        localStorage.getItem("isTwilioDisabled") !== null &&
          localStorage.getItem("isTwilioDisabled") === "true",
        localStorage.getItem("countryCodeForRcs") || ""
      );

      props.setPopup({
        customPopupMessage: true,
        messageType: "success",
        message: pathOr("Something went wrong", ["data", "message"], response),
      });

      setState((st) => ({ ...st, sendPopup: false }));

      const data = await fetchReports(state.onMountFilters);

      setState((st) => ({ ...st, tableData: data }));
    } catch (err) {
      props.setPopup({
        customPopupMessage: true,
        messageType: "error",
        message: pathOr(
          "Something went wrong",
          ["response", "data", "message"],
          err
        ),
      });

      setState((st) => ({ ...st, sendPopup: false }));
    }
  };

  const updateBroadcastReportStatus = (data) => {
    setState((st) => ({ ...st, updateTable: !state.updateTable }));
  };

  const copyToClipboard = (text) => {
    var dummy = document.createElement("input");
    document.body.appendChild(dummy);
    dummy.setAttribute("value", text);
    dummy.select();
    document.execCommand("copy");
    document.body.removeChild(dummy);
    handlePopupMessage();
  };

  const handlePopupMessage = () => {
    props.setPopup({
      customPopupMessage: true,
      messageType: "success",
      message: "File Name has been copied to clipboard!",
    });
  };

  const checkToken = async () => {
    const decodedToken = loginService.getDecodedToken();
    const user = decodedToken && decodedToken.payload;
    const { liveChatAccess, broadcastAccess, reportOnlyAccess } = user || {};

    setState((prevState) => ({ ...prevState, broadcastAccess }));
    if (!reportOnlyAccess) {
      if (!broadcastAccess && !liveChatAccess) {
        history.push("/chat-agent");
      }
      if (!broadcastAccess) {
        history.push("/chat");
      }
    }
  };

  useEffect(() => {
    checkToken();

    // socket event listener for socket service
    window.addEventListener(
      "updateBroadcastReportStatus",
      (e) => {
        updateBroadcastReportStatus(e.detail);
      },
      false
    );

    return () => {
      window.removeEventListener(
        "updateBroadcastReportStatus",
        updateBroadcastReportStatus
      );
    };
  }, []);

  const showMessageLog = async () => {
    history.push("/broadcast/messageLog");
  };

  const getCsvFileName = (textFileName) => {
    if (textFileName) {
      return state.filename;
    } else {
      return `${state.filename ? state.filename : "Broadcast_Report"}.txt`;
    }
  };

  const { customPopupMessage, messageType, message } = props.popupReducer;
  const textFileName = state.filename.includes(".txt");

  const decodedToken = loginService.getDecodedToken();
  const loggedinUser = decodedToken?.payload;

  const showMessageLogButton = () => {
    if (
      loggedinUser?.liveChatAccess &&
      loggedinUser?.broadcastAccess &&
      loggedinUser?.reportOnlyAccess
    ) {
      return false;
    } else if (loggedinUser?.reportOnlyAccess) {
      return true;
    }
    return false;
  };

  return (
    <Layout testId="broadcast-report-page-wrapper">
      {customPopupMessage && (
        <CustomNotification messageType={messageType} message={message} />
      )}
      {state.confirmPopup && (
        <ConfirmPopup
          titleMessage="Confirm to cancel broadcast"
          handleCancel={() =>
            setState((st) => ({ ...st, confirmPopup: false }))
          }
          handleDelete={handleDeleteBroadcast}
          isVisible={state.confirmPopup}
          submitButtonText="Yes"
          cancelButtonText="No"
          message="Are you sure you want to cancel this broadcast?"
        />
      )}

      {state.killPopup && (
        <ConfirmPopup
          titleMessage="Confirm to stop broadcast"
          handleCancel={() => setState((st) => ({ ...st, killPopup: false }))}
          handleDelete={killBroadcast}
          isVisible={state.killPopup}
          submitButtonText="Yes"
          cancelButtonText="No"
          message="Are you sure you want to stop this broadcast?"
        />
      )}

      {state.editBroadcast && (
        <AddEditSchedule
          title="Edit Broadcast"
          hideScheduleModal={async () => {
            setState((st) => ({ ...st, editBroadcast: false }));
            const data = await fetchReports(state.onMountFilters);
            setState((st) => ({ ...st, tableData: data }));
          }}
          edit={true}
          isVisible={state.editBroadcast}
          handleCancel={() =>
            setState((st) => ({ ...st, editBroadcast: false }))
          }
          values={state.record}
        />
      )}

      {state.sendPopup && (
        <ConfirmPopup
          titleMessage="Confirm to Send Broadcast Now"
          handleCancel={() => setState((st) => ({ ...st, sendPopup: false }))}
          handleDelete={sendNow}
          isVisible={state.sendPopup}
          submitButtonText="Yes"
          cancelButtonText="No"
          message="Are you sure you want to broadcast the message now?"
        />
      )}
      <div className="header-bar-mob">
        <MobileHeader
          showLogo={false}
          showNotifications={false}
          showHelp={false}
          showAccountDetails={false}
          backgroundColor="gray-bg"
          showBackButton={true}
          showPageTitle={true}
          pageTitle="Broadcast Report"
          handleBackButton={hideReport}
        />
      </div>
      <div className="inner-content-wrapper broadcast-report-wrapper">
        {showMessageLogButton() &&
          width < MOBILE_WIDTH && (
            <div className="broadcast-report-nav">
              <span
                onClick={() => {
                  history.push("/broadcast/messageLog");
                }}
              >
                Go to Message Log
              </span>
            </div>
          )}
        <Card
          className="primary-card live-chat-card patient-report-card"
          title="Broadcast"
          extra={
            <div className="close-container">
              {!!state.broadcastAccess && (
                <CustomButton
                  btntype="clear"
                  title="Close"
                  onClick={hideReport}
                  className="mr-8"
                />
              )}
              {(loggedinUser?.lcUser?.roles?.roleKey === "BA" ||
                loggedinUser?.roles?.roleKey === "BA") && (
                <CustomButton
                  title={
                    <div className="icon-button">
                      <span className="message-icon">
                        <MessageLogIcon />
                      </span>
                      Message Log
                    </div>
                  }
                  tooltip="Message Log"
                  btntype="primary"
                  className={
                    "cancel-button ml-8 reports-button message-log-button"
                  }
                  onClick={showMessageLog}
                />
              )}
              <div className="selected-report">{selectedFile}</div>
            </div>
          }
        >
          <CommonTable
            title="Broadcast Report"
            columns={broadcastReportsColumns(
              downloadFiles,
              handlePopUp,
              copyToClipboard,
              loggedinUser
            )}
            fetchData={fetchReports}
            getSuggestions={getSuggestions}
            tableData={state.tableData}
            updateTable={state.updateTable}
          />
          <CSVLink
            filename={getCsvFileName(textFileName)}
            id={state.csvData}
            data={state.csvData}
            className="hidden"
            target="_blank"
            ref={csvLink}
          />
        </Card>
      </div>
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  popupReducer: state.popupReducer,
});
const mapDispatchToProps = (dispatch) => {
  return {
    setPopup: (data) => dispatch(setPopup(data)),
    downloadPatientFiles: (object, cb) => {
      dispatch(downloadPatientFilesSagaAction(object, cb));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BroadcastReport);
